import React from 'react';
import { Box, Typography, Paper, useMediaQuery, useTheme, List, ListItem, ListItemText, Divider } from '@mui/material';
import { MergedHoliday } from '../types';
import { HolidayPill } from './HolidayPill';

interface CalendarProps {
  year: number;
  month: number; // 1-12
  holidays: MergedHoliday[];
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_NAMES_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const Calendar: React.FC<CalendarProps> = ({ year, month, holidays }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const firstDayOfMonth = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();

  // Create a map of date -> holidays for quick lookup
  const holidaysByDate = React.useMemo(() => {
    const map = new Map<number, MergedHoliday[]>();
    holidays.forEach((holiday) => {
      const day = parseInt(holiday.dateString.substring(6, 8));
      if (!map.has(day)) {
        map.set(day, []);
      }
      map.get(day)!.push(holiday);
    });
    return map;
  }, [holidays]);

  // Generate calendar grid
  const calendarDays = [];
  
  // Empty cells before first day of month
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(null);
  }
  
  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  const isToday = (day: number | null) => {
    if (!day) return false;
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === month - 1 &&
      today.getFullYear() === year
    );
  };

  // Mobile list view
  if (isMobile) {
    // Group holidays by date
    const daysWithHolidays: Array<{ day: number; dayOfWeek: string; holidays: MergedHoliday[] }> = [];
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dayHolidays = holidaysByDate.get(day) || [];
      if (dayHolidays.length > 0) {
        const date = new Date(year, month - 1, day);
        const dayOfWeek = DAY_NAMES_FULL[date.getDay()];
        daysWithHolidays.push({ day, dayOfWeek, holidays: dayHolidays });
      }
    }

    if (daysWithHolidays.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No holidays this month
          </Typography>
        </Box>
      );
    }

    return (
      <List sx={{ width: '100%', p: 0 }}>
        {daysWithHolidays.map(({ day, dayOfWeek, holidays: dayHolidays }, index) => {
          const today = isToday(day);
          
          return (
            <React.Fragment key={day}>
              <ListItem
                sx={{
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  py: 2,
                  px: 2,
                  bgcolor: today ? 'primary.light' : 'transparent',
                  border: today ? '2px solid' : 'none',
                  borderColor: today ? 'primary.main' : 'transparent',
                  borderRadius: today ? 1 : 0,
                  mb: today ? 1 : 0,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, width: '100%' }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: today ? 'primary.dark' : 'text.primary',
                      mr: 2,
                      minWidth: '40px',
                    }}
                  >
                    {day}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: today ? 'primary.dark' : 'text.secondary',
                      fontWeight: today ? 500 : 400,
                    }}
                  >
                    {dayOfWeek}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                  {dayHolidays.map((holiday) => (
                    <HolidayPill key={holiday.id} holiday={holiday} />
                  ))}
                </Box>
              </ListItem>
              {index < daysWithHolidays.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </List>
    );
  }

  // Desktop calendar grid view
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexShrink: 0 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {MONTH_NAMES[month - 1]} {year} 
        </Typography>
        <Typography variant="body2" color="text.secondary" style={{ fontStyle: 'italic' }}>
          {holidays.length} {holidays.length === 1 ? "holiday" : "holidays"} in {MONTH_NAMES[month - 1]}
        </Typography>
      </Box>

      {/* Day headers */}
      <Box sx={{ display: 'flex', mb: 1, flexShrink: 0 }}>
        {DAY_NAMES.map((day) => (
          <Box
            key={day}
            sx={{
              flex: '1 1 14.28%',
              textAlign: 'center',
              fontWeight: 600,
              color: 'text.secondary',
              fontSize: '0.875rem',
              py: 1,
            }}
          >
            {day}
          </Box>
        ))}
      </Box>

      {/* Calendar grid */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 1,
        flex: 1,
        minHeight: 0,
        autoRows: '1fr',
      }}>
        {calendarDays.map((day, index) => {
          const dayHolidays = day ? holidaysByDate.get(day) || [] : [];
          const hasHolidays = dayHolidays.length > 0;
          const today = isToday(day);

          return (
            <Paper
              key={index}
              elevation={hasHolidays ? 2 : 0}
              sx={{
                flex: '1 1 auto',
                minHeight: 0,
                p: 1,
                bgcolor: day ? (today ? 'primary.light' : 'background.paper') : 'transparent',
                border: day ? (today ? '2px solid' : '1px solid') : 'none',
                borderColor: day ? (today ? 'primary.main' : 'divider') : 'transparent',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.2s',
                opacity: day ? 1 : 0,
                overflow: 'hidden',
                minWidth: 0,
                '&:hover': day
                  ? {
                      boxShadow: 2,
                      transform: 'translateY(-2px)',
                    }
                  : {},
              }}
            >
              {day && (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: today ? 700 : hasHolidays ? 600 : 400,
                      color: today ? 'primary.dark' : hasHolidays ? 'primary.main' : 'text.primary',
                      mb: 0.5,
                    }}
                  >
                    {day}
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 0.5, 
                    overflow: 'auto', 
                    flex: 1,
                    minWidth: 0,
                  }}>
                    {dayHolidays.map((holiday) => (
                      <HolidayPill key={holiday.id} holiday={holiday} />
                    ))}
                  </Box>
                </>
              )}
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};
